import { useState, useEffect } from 'react';
import { api } from '../../../services/api';

interface HabitType {
  id: string;
  nom: string;
  prix: number;
  icon: string;
}

interface SelectedItem {
  type_habit_id: string;
  nom: string;
  prix_unitaire: number;
  quantite: number;
  tissus?: any[];
}

interface Props {
  selectedItems: SelectedItem[];
  onChange: (items: SelectedItem[]) => void;
  isTissusBeforeCouture?: boolean;
  orderFabrics?: any[];
  stock?: any[];
}

export default function StepTypeCouture({ selectedItems, onChange, isTissusBeforeCouture = false, orderFabrics = [], stock = [] }: Props) {
  const [types, setTypes] = useState<HabitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchTypes = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (token) {
        try {
          const data = await api.getHabitTypes(token);
          setTypes(data);
        } catch (e) {
          console.error("Erreur chargement types habit", e);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchTypes();
  }, []);

  const updateQty = (type: HabitType, delta: number) => {
    let newItems = [...selectedItems];

    if (delta > 0) {
      newItems.push({
        type_habit_id: type.id,
        nom: type.nom,
        prix_unitaire: Number(type.prix) || 0,
        quantite: 1,
        tissus: []
      });
    } else if (delta < 0) {
      const lastIndex = newItems.map(i => i.type_habit_id).lastIndexOf(type.id);
      if (lastIndex !== -1) {
        newItems.splice(lastIndex, 1);
      }
    }

    // Renommer dynamiquement les articles correspondants pour refléter la séquence (ex: 1/3, 2/3...)
    const matchingIndices: number[] = [];
    newItems.forEach((item, idx) => {
      if (item.type_habit_id === type.id) {
        matchingIndices.push(idx);
      }
    });

    const count = matchingIndices.length;
    matchingIndices.forEach((itemIdx, seq) => {
      newItems[itemIdx].nom = count > 1 ? `${type.nom} (${seq + 1}/${count})` : type.nom;
    });

    onChange(newItems);
  };

  const toggleFabricForItem = (itemIndex: number, fabric: any) => {
    const newItems = [...selectedItems];
    const item = newItems[itemIndex];
    if (!item.tissus) item.tissus = [];
    
    const exists = item.tissus.some(t => t.tempId === fabric.tempId);
    if (exists) {
      item.tissus = item.tissus.filter(t => t.tempId !== fabric.tempId);
    } else {
      item.tissus.push({
        tempId: fabric.tempId,
        is_client_cloth: fabric.is_client_cloth,
        description: fabric.description,
        tissu_stock_id: fabric.tissu_stock_id,
        quantite: fabric.quantite || 0
      });
    }
    onChange(newItems);
  };

  const updateFabricQtyForItem = (itemIndex: number, fabricTempId: string, qty: number) => {
    const newItems = [...selectedItems];
    const item = newItems[itemIndex];
    if (item.tissus) {
      item.tissus = item.tissus.map(t => {
        if (t.tempId === fabricTempId) {
          return { ...t, quantite: qty };
        }
        return t;
      });
      onChange(newItems);
    }
  };

  const filteredTypes = types.filter(t => t.nom.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) return <div className="p-8 text-center text-gray-500">Chargement des types de couture...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-xl border border-gray-100">
        <i className="ri-search-line text-gray-400 ml-2" />
        <input 
          type="text" 
          placeholder="Rechercher un type (Boubou, Pantalon...)" 
          className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-2"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {filteredTypes.map(t => {
          const selected = selectedItems.find(si => si.type_habit_id === t.id);
          return (
            <div key={t.id} className={`relative p-4 rounded-2xl border-2 transition-all cursor-pointer group ${
              selected ? 'border-foret-500 bg-foret-50' : 'border-gray-100 hover:border-gray-200 bg-white'
            }`}>
              <div className="flex flex-col items-center text-center gap-2">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                    selected ? 'bg-foret-500 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  <i className={t.icon || 'ri-t-shirt-line'} />
                </div>
                <h4 className="font-bold text-sm text-gray-800 line-clamp-1">{t.nom}</h4>
                <p className="text-xs font-bold text-foret-600">{Number(t.prix).toLocaleString()} FCFA</p>
              </div>

              {selected ? (
                <div className="mt-4 flex items-center justify-center gap-3">
                  <button 
                    onClick={(e) => { e.stopPropagation(); updateQty(t, -1); }} 
                    className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100 text-gray-600"
                    title="Diminuer la quantité"
                    aria-label="Diminuer la quantité"
                  >
                    <i className="ri-subtract-line" />
                  </button>
                  <span className="font-bold text-sm text-gray-900">{selectedItems.filter(si => si.type_habit_id === t.id).length}</span>
                  <button 
                    onClick={(e) => { e.stopPropagation(); updateQty(t, 1); }} 
                    className="w-8 h-8 rounded-lg bg-foret-500 text-white flex items-center justify-center hover:bg-foret-600"
                    title="Augmenter la quantité"
                    aria-label="Augmenter la quantité"
                  >
                    <i className="ri-add-line" />
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => updateQty(t, 1)}
                  className="mt-4 w-full py-2 rounded-lg border border-gray-200 text-xs font-bold text-gray-500 group-hover:bg-foret-500 group-hover:text-white group-hover:border-foret-500 transition-colors"
                >
                  Ajouter
                </button>
              )}
            </div>
          );
        })}
      </div>

      {isTissusBeforeCouture && orderFabrics.length > 0 && selectedItems.length > 0 && (
        <div className="bg-white rounded-3xl border border-gray-100 p-6 space-y-6 shadow-sm mt-6">
          <div>
            <h3 className="font-playfair font-bold text-lg text-gray-900 flex items-center gap-2">
              <i className="ri-palette-line text-foret-600" />
              Association des Tissus aux Coutures
            </h3>
            <p className="text-xs text-gray-400 mt-1">
              Cochez les tissus associés à chaque habit et réajustez le métrage nécessaire.
            </p>
          </div>

          <div className="space-y-6 divide-y divide-gray-50">
            {selectedItems.map((item, itemIdx) => (
              <div key={`${item.type_habit_id}_${itemIdx}`} className="pt-5 first:pt-0 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-foret-500" />
                  <h4 className="font-bold text-sm text-gray-800 uppercase tracking-wide">
                    {item.nom} ({item.quantite} pièce{item.quantite > 1 ? 's' : ''})
                  </h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-4">
                  {orderFabrics.map((fabric) => {
                    const isChecked = item.tissus?.some(t => t.tempId === fabric.tempId);
                    const assignedFabric = item.tissus?.find(t => t.tempId === fabric.tempId);
                    
                    let fabricLabel = '';
                    if (fabric.is_client_cloth) {
                      fabricLabel = fabric.description || 'Tissu client (sans description)';
                    } else {
                      const s = stock?.find(st => String(st.id) === String(fabric.tissu_stock_id));
                      fabricLabel = s ? `Tissu boutique : ${s.nom}` : 'Tissu boutique sélectionné';
                    }

                    return (
                      <div key={fabric.tempId} className={`p-4 rounded-2xl border-2 transition-all flex flex-col gap-3 ${
                        isChecked ? 'border-foret-500 bg-foret-50/20' : 'border-gray-100 bg-white hover:border-gray-200'
                      }`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input 
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-300 text-foret-600 focus:ring-foret-500 mt-0.5 cursor-pointer"
                            checked={isChecked || false}
                            onChange={() => toggleFabricForItem(itemIdx, fabric)}
                          />
                          <span className="text-xs font-bold text-gray-700 leading-tight flex-1">{fabricLabel}</span>
                        </label>

                        {isChecked && assignedFabric && (
                          <div className="animate-in fade-in slide-in-from-top-1 pl-7">
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Métrage pour cet habit (m)</label>
                            <input 
                              type="number"
                              step="0.1"
                              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-foret-500 focus:border-transparent outline-none font-bold text-xs bg-white"
                              value={assignedFabric.quantite}
                              onChange={e => updateFabricQtyForItem(itemIdx, fabric.tempId, Number(e.target.value))}
                              placeholder="0.0"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedItems.length > 0 && (
          <div className="bg-foret-900 text-white p-4 rounded-2xl shadow-lg flex items-center justify-between">
              <div>
                  <p className="text-[10px] uppercase font-bold text-foret-300">Sélection</p>
                  <p className="text-sm font-bold">{selectedItems.length} type(s) sélectionné(s)</p>
              </div>
              <div className="text-right">
                  <p className="text-[10px] uppercase font-bold text-foret-300">Total estimé</p>
                  <p className="text-xl font-playfair font-bold">
                      {(() => {
                        let total = 0;
                        selectedItems.forEach(item => {
                          total += (Number(item.prix_unitaire) || 0) * (Number(item.quantite) || 0);
                          if (item.tissus && Array.isArray(item.tissus)) {
                            item.tissus.forEach((t: any) => {
                              if (!t.is_client_cloth && t.tissu_stock_id) {
                                const s = stock?.find(st => String(st.id) === String(t.tissu_stock_id));
                                if (s) {
                                  total += (Number(s.prix_unitaire) || 0) * (Number(t.quantite) || 0);
                                }
                              }
                            });
                          }
                        });
                        return total.toLocaleString();
                      })()} <span className="text-xs">FCFA</span>
                  </p>
              </div>
          </div>
      )}

      {filteredTypes.length === 0 && (
          <div className="p-8 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
              <p className="text-sm text-gray-500 mb-4">Aucun type trouvé pour "{searchTerm}"</p>
              <button className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-100">
                  <i className="ri-add-line mr-2" />
                  Créer un nouveau type personnalisé
              </button>
          </div>
      )}
    </div>
  );
}
